import React, { useEffect, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import styled from 'styled-components';
import { verifyGhlEmbedAccess } from '../services/api';
import { useLogoutHandler } from '../utils/logoutHandler';
import WhatsAppLogoutDialog from '../components/WhatsAppLogoutDialog';
import WhatZProFlow from '../assets/images/WhatZProFlow.mp4';



// Styled Components
const Container = styled.div<{ $embed?: boolean }>`
  min-height: ${props => (props.$embed ? 'auto' : '100vh')};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => (props.$embed ? '20px 16px' : '40px 20px')};
  background: white;
`;

const WarningBanner = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  padding: 12px 20px;
  text-align: center;
  width: 100%;
  max-width: 600px;
  margin-bottom: 40px;
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  gap: 40px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RightSection = styled.div`
  flex: 1;
  width: 100%;
  max-width: 600px;
`;

const QRContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const QRImage = styled.img`
  width: 300px;
  height: 300px;
  margin-bottom: 20px;
`;

const QRPlaceholder = styled.div`
  width: 300px;
  height: 300px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const InstructionText = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  padding: 12px 20px;
  text-align: center;
  width: 100%;
  max-width: 400px;
  margin: 20px 0;
`;

const VideoContainer = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 16/9;
  max-height: 400px;

  iframe, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NotesSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-top: 40px;
`;

const NotesTitle = styled.h2`
  color: #dc3545;
  font-size: 24px;
  margin-bottom: 20px;
`;

const NoteItem = styled.div`
  margin-bottom: 20px;

  strong {
    color: #333;
  }

  span {
    color: #666;
  }
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  padding: 8px 24px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background: #c82333;
  }
`;

// Styled Components for Status Message
const StatusMessage = styled.div<{ status: string }>`
  padding: 12px 20px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  margin-top: 20px;
  width: 100%;
  max-width: 500px;
  margin: 20px auto;

  background-color: ${(props) =>
    props.status === 'connected'
      ? 'rgb(37, 121, 65)'
      : props.status === 'connecting'
        ? ' #856404'
        : props.status === 'reconnecting'
          ? '#007bff'
          : '#dc3545'}; 

  color: white;

  span {
    font-weight: normal;
  }
`;
const ResetQRButton = styled.button`
  background: #007bff;
  color: white;
  padding: 8px 24px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;
  margin-top: 10px;

  &:hover {
    background: #0056b3;
  }
`;

const QuestionMarkImage = styled.div`
  width: 300px;
  height: 300px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 120px;
  color: #dc3545;
`;

const SubscriptionErrorContainer = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
`;

const ErrorIcon = styled.div`
  width: 120px;
  height: 120px;
  background: #ff6b6b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 48px;
  color: white;
`;

const ErrorTitle = styled.h2`
  color: #333;
  font-size: 24px;
  margin-bottom: 16px;
  font-weight: 600;
`;

const ErrorDescription = styled.div`
  color: #666;
  text-align: center;
  margin-bottom: 32px;
  font-size: 16px;
  line-height: 1.5;
`;

const RedirectButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  color: white;
  padding: 12px 32px;
  border-radius: 25px;
  border: none;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);

  &:hover {
    background: linear-gradient(135deg, #ff8e53, #ff6b6b);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
  }
`;

const ErrorText = styled.div`
  color: #dc3545;
  text-align: center;
  margin-top: 10px;
  font-weight: 500;
`;

// Helper to extract param
const getLocationIdFromParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('locationId');
};

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://whatzproapi.theonetechnologies.co.in'
    : 'http://localhost:5555');

const QRScan: React.FC<{ embedMode?: boolean }> = ({ embedMode = false }) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [embedLocationName, setEmbedLocationName] = useState<string | null>(null);
  const [embedReady, setEmbedReady] = useState(!embedMode);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubscriptionError, setIsSubscriptionError] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(true);
  const [connectionState, setConnectionState] = useState<{ status: string; attempt?: number; maxAttempts?: number }>({ status: 'disconnected' });

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm: baseHandleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  useEffect(() => {
    const locId = getLocationIdFromParams();
    const embedToken = new URLSearchParams(window.location.search).get('token');
    setLocationId(locId);

    if (!locId) {
      const errorMsg = 'No location ID provided';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    let cancelled = false;
    let newSocket: Socket | null = null;

    const connectSocket = () => {
      newSocket = io(SOCKET_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        extraHeaders: { 'ngrok-skip-browser-warning': 'true' }
      });

      newSocket.on('connect', () => {
        console.log('[QRScan] Socket connected to', SOCKET_URL);
        setSocketConnected(true);
        setError(null);
        setConnectionState({ status: 'connecting' });
        newSocket?.emit('register', locId);
      });

      newSocket.on('connect_error', (err) => {
        console.error('[QRScan] Socket connect_error:', err.message);
        setSocketConnected(false);
        setError(`Cannot reach server: ${err.message}. Check that the API is running and WebSocket is enabled.`);
        toast.error('Socket connection failed');
      });

      newSocket.on('disconnect', () => {
        setSocketConnected(false);
        setConnectionState({ status: 'disconnected' });
        if (!embedMode) {
          toast.warning('Disconnected from server, attempting to reconnect...');
        }
      });

      newSocket.on(`qr-${locId}`, qr => {
        if (typeof qr === 'string') {
          const applyQr = (url: string) => {
            setQrCode(url);
            setError(null);
            setConnectionState({ status: 'connecting' });
          };

          if (qr.startsWith('data:image')) {
            applyQr(qr);
          } else {
            QRCode.toDataURL(qr)
              .then(applyQr)
              .catch(err => {
                console.log('Error generating QR code:', err);
                setError('Error generating QR code');
                toast.error('Error generating QR code');
              });
          }
        }
      });

      newSocket.on(`qr-timeout-${locId}`, data => {
        setQrCode(null);
        setError(data.message);
        toast.error(data.message);
      });

      newSocket.on(`whatsapp-connected-${locId}`, () => {
        sessionStorage.setItem(`wa-connected-${locId}`, '1');
        setIsConnected(true);
        setError(null);
        setConnectionState({ status: 'connected' });
      });

      newSocket.on(`whatsapp-disconnected-${locId}`, () => {
        sessionStorage.removeItem(`wa-connected-${locId}`);
        setIsConnected(false);
        setConnectionState({ status: 'disconnected' });
      });

      newSocket.on(`whatsapp-error-${locId}`, data => {
        setIsConnected(false);
        setConnectionState({ status: 'disconnected' });
        setError(data.reason || 'WhatsApp error occurred');

        if (data.reason === 'No active subscription to access this feature') {
          setIsSubscriptionError(true);
        }

        toast.error(data.reason || 'WhatsApp error occurred');
      });

      newSocket.on(`whatsapp-reconnecting-${locId}`, data => {
        setConnectionState({ status: 'reconnecting', attempt: data.attempt, maxAttempts: data.maxAttempts });
        toast.info(`Reconnecting... Attempt ${data.attempt}/${data.maxAttempts}`);
      });

      newSocket.on('qr-error', ({ message }) => {
        setError(message);
        toast.error(message || 'Error fetching QR code');
      });

      setSocket(newSocket);
    };

    const bootstrap = async () => {
      if (embedMode) {
        if (!embedToken) {
          setError('Invalid setup link. Please use the link from your WhatzPro email.');
          return;
        }

        try {
          const verified = await verifyGhlEmbedAccess(locId, embedToken);
          if (cancelled) return;
          setEmbedLocationName(verified.locationName);
          setEmbedReady(true);
          setHasActiveSubscription(true);
          connectSocket();
        } catch (err) {
          if (cancelled) return;
          const message = err instanceof Error ? err.message : 'Invalid or expired setup link';
          setError(message);
          toast.error(message);
        }
        return;
      }

      try {
        const token = localStorage.getItem('token');
        setHasActiveSubscription(Boolean(token));
      } catch {
        setHasActiveSubscription(false);
      }

      connectSocket();
    };

    bootstrap();

    return () => {
      cancelled = true;
      if (newSocket) {
        newSocket.off();
        newSocket.disconnect();
      }
    };
  }, [embedMode]);

  const handleResetQR = () => {
    if (locationId && socket?.connected) {
      setQrCode(null);
      setIsConnected(false);
      setError(null);
      setConnectionState({ status: 'connecting' });
      // 'request-qr' tells the server to wipe old auth files and generate a fresh QR
      socket.emit('request-qr', locationId);
      toast.info('Requesting new QR code...');
    } else {
      toast.error('Not connected to server');
    }
  };

  const handleLogoutConfirm = () => {
    if (locationId && socket?.connected) {
      socket.emit('logout', locationId);
      setQrCode(null);
      setIsConnected(false);
      setError(null);
      setConnectionState({ status: 'connecting' });
      toast.info('Logged out — generating new QR code...');
    }
    handleLogoutCancel();
  };

  const handleLogout = () => {
    handleLogoutClick();
  };

  const getStatusMessage = () => {
    switch (connectionState.status) {
      case 'connected': return 'Connected to WhatsApp';
      case 'connecting': return 'Connecting to WhatsApp';
      case 'reconnecting': return `Reconnecting (${connectionState.attempt}/${connectionState.maxAttempts})`;
      default: return 'Disconnected';
    }
  };

  return (
    <Container $embed={embedMode}>
      {!embedMode && (
        <WarningBanner>Don't turn off your scanner camera before status CONNECTED</WarningBanner>
      )}

      {embedMode && embedLocationName && (
        <WarningBanner style={{ background: '#fff7f5', borderColor: 'rgba(255,107,107,0.2)', color: '#334155' }}>
          WhatsApp setup for <strong>{embedLocationName}</strong>
        </WarningBanner>
      )}

      {!embedReady ? (
        <QRPlaceholder>
          <p>Verifying your secure setup link...</p>
        </QRPlaceholder>
      ) : (
      <ContentWrapper>
        <LeftSection>
          <QRContainer>
            {isConnected ? (
              <div>
                <StatusMessage status={connectionState.status}>
                  <div>{getStatusMessage()}</div>
                </StatusMessage>
              </div>
            ) : !hasActiveSubscription || isSubscriptionError ? (
              <SubscriptionErrorContainer>
                <ErrorIcon>⚠️</ErrorIcon>
                <ErrorTitle>Subscription Required</ErrorTitle>
                <ErrorDescription>
                  You need an active subscription to access this feature.
                  Please subscribe to continue using WhatsApp.
                </ErrorDescription>
                <RedirectButton onClick={() => window.location.href = '/subscribed-locations'}>
                  Get Subscription
                </RedirectButton>
              </SubscriptionErrorContainer>
            ) : qrCode ? (
              <>
                <QRImage src={qrCode} alt="WhatsApp QR Code" />
                <InstructionText>Please scan with your WhatsApp Account</InstructionText>
                <ResetQRButton onClick={handleResetQR}>Reset QR Code</ResetQRButton>
              </>
            ) : error ? (
              <>
                <QRPlaceholder>
                  <p style={{ color: '#dc3545', textAlign: 'center', padding: '0 16px' }}>{error}</p>
                </QRPlaceholder>
                <ResetQRButton onClick={handleResetQR}>Reset QR Code</ResetQRButton>
              </>
            ) : (
              <>
                <QRPlaceholder>
                  <p>Loading QR code...</p>
                </QRPlaceholder>
                <ResetQRButton onClick={handleResetQR} style={{ marginTop: '10px' }}>
                  Not loading? Reset QR Code
                </ResetQRButton>
              </>
            )}
          </QRContainer>
        </LeftSection>

        {!embedMode && (
        <RightSection>
          <VideoContainer>
            <video
              src={WhatZProFlow}
              title="WhatZProFlow Video"
              controls
              autoPlay
              loop
              muted
            />
          </VideoContainer>
        </RightSection>
        )}
      </ContentWrapper>
      )}

      {!embedMode && (
      <NotesSection>
        <NotesTitle>Note:-</NotesTitle>
        <NoteItem>
          <strong>1. How to change connected mobile number:</strong>{" "}
          <span>
            if you want to change the mobile number then click on the LOGOUT
            button and scan the QR code from the new whatsapp number, you may need
            to scan the QR code twice.
          </span>
        </NoteItem>
        <NoteItem>
          <strong>2. QR code not loading:</strong>{" "}
          <span>
            if you are not able to see the QR code, then click on the RESET QR
            CODE button, and you will get the QR code. Scan the QR code to get
            connected, you may need to scan the QR code twice.
          </span>
        </NoteItem>
      </NotesSection>
      )}

      {isConnected && !embedMode && (
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      )}

      <WhatsAppLogoutDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </Container>
  );

};

export default QRScan;